import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { IPagedQueryOptions } from "../library/models/page";
import {
    AppointmentScope,
    AppointmentStatus,
    CreateAppointmentRequest,
    DeclineAppointmentRequest,
    EmptySlotResponse,
    IAppointment,
    IAppointmentPaymentOptions,
    IPendingRequestCount,
    RescheduleAppointmentRequest,
} from "../library/models/appointment";
import { BaseService } from "./baseService";

/**
 * The clinic's diary.
 *
 * Tenant-scoped like every other service here — which clinic comes from the token, never
 * from an argument. Who may see what is decided on the server too: a therapist is pinned
 * to their own column and a patient to their own appointments, so passing somebody else's
 * id is refused rather than quietly narrowed. There is deliberately no way to ask for a
 * colleague's *diary* from here — but `empty-slots` is the deliberate exception and always
 * was: booking a patient in with a colleague needs that colleague's free time, so it answers
 * with free windows and never with what fills the busy ones. Availability without content.
 *
 * Several of these are factory methods taking an id rather than plain mutations, because
 * `BaseService.createMutation` bakes its URL in at construction and these endpoints are
 * nested under an appointment. The deletes are hand-rolled for a second reason: the helper
 * sends its variable as `?id=`, which is not the shape of `/appointments/{id}`.
 */
/** The staff-only widenings of a slot search. All default off. */
export interface IEmptySlotOptions {
    /** Walk the whole day, not just the therapist's program. Ignored for a patient caller. */
    includeOutsideProgram?: boolean;
    /**
     * Offer a start every this many minutes, aligned to the hour, rather than back-to-back
     * windows. Must divide 60. Ignored for a patient caller.
     */
    startStepMinutes?: number;
    /** Read past this appointment, so it does not occupy the hour it is being moved out of. */
    excludeAppointmentId?: number;
    /**
     * The catalogue line being booked, so an hour is not offered while every room it needs is
     * taken, by whichever therapist. Without it the answer is the therapist's free time alone.
     * Not needed with `excludeAppointmentId`: the API reads the room off that appointment.
     */
    serviceId?: number;
}

export class AppointmentsService extends BaseService {
    /**
     * A window of the diary, inclusive of both dates.
     *
     * `therapistId` and `patientId` narrow it for an admin. A therapist or a patient can
     * leave both off and get their own — the server pins it either way.
     *
     * `statuses` narrows what comes back and settles nothing about access. A calendar
     * passes `OCCUPYING_STATUSES`, so a cancelled booking stops being drawn over the slot
     * it gave back; a history leaves it off and gets all four. Sent **joined** rather than
     * as an array: axios would serialise `['A','B']` as `statuses[]=A&statuses[]=B`, which
     * Spring does not bind to a `List<AppointmentStatus>`.
     */
    getAppointments(
        from: string,
        to: string,
        therapistId?: number,
        patientId?: number,
        statuses?: AppointmentStatus[]
    ): UseQueryOptions<IAppointment[], Error> {
        return this.createGetQuery<IAppointment[]>('/appointments', {
            from,
            to,
            ...(therapistId ? { therapistId } : {}),
            ...(patientId ? { patientId } : {}),
            ...(statuses?.length ? { statuses: statuses.join(',') } : {}),
        });
    }

    /**
     * One page of somebody's appointments, in the half of their history they asked for.
     *
     * Paged rather than a window, unlike `getAppointments`: a calendar needs every row in
     * its range at once to draw a grid, while a history grows for as long as a patient
     * attends. My Appointments used to ask for a year of it and cut the answer up in the
     * browser -- paying the whole transfer to draw a screenful, and quietly losing anything
     * older than six months.
     *
     * `scope` splits it and `statuses` narrows it, both in SQL. Every filter is folded into
     * the query's `data`, which makes it part of the query key for free, so React Query
     * caches per filter and going back to one already asked for is instant. Blocks of time
     * never come back at all -- they belong to nobody, and removing one is the calendar's
     * job.
     *
     * `statuses` is joined rather than passed as an array, for the same reason
     * `getAppointments` joins it: axios would serialise `['A','B']` as
     * `statuses[]=A&statuses[]=B`, which Spring does not bind to a `List<AppointmentStatus>`.
     */
    getAppointmentHistory(
        size: number,
        filters: { scope: AppointmentScope; statuses?: AppointmentStatus[] }
    ): IPagedQueryOptions<IAppointment> {
        const { scope, statuses } = filters;

        return this.createGetInfiniteQuery<IAppointment>('/appointments/history', size, {
            scope,
            ...(statuses?.length ? { statuses: statuses.join(',') } : {}),
        });
    }

    /**
     * One page of the signed-in therapist's booking requests still waiting for an answer,
     * soonest first. Pinned to the caller on the server, so there is nothing to pass: only
     * the therapist a request was made of may answer it. A request past its deadline is
     * already left out.
     */
    getPendingRequests(size: number): IPagedQueryOptions<IAppointment> {
        return this.createGetInfiniteQuery<IAppointment>('/appointments/requests', size);
    }

    /**
     * How many that list holds, for the badge in the header. Its own URL and so its own
     * query key — invalidating the list does not refresh it, which is why `useRefreshDiary`
     * names both.
     */
    getPendingRequestCount(): UseQueryOptions<IPendingRequestCount, Error> {
        return this.createGetQuery<IPendingRequestCount>('/appointments/requests/count');
    }

    /**
     * Free windows of a fixed length, between whatever already occupies the therapist's time.
     *
     * The two options are both staff concerns and both default off, so the patient's wizard
     * asks exactly what it always asked.
     *
     * `includeOutsideProgram` walks the whole day rather than the therapist's opening hours,
     * days they do not work included. It is what lets a *therapist's* own dialog offer a list
     * at all: the API applies its availability rule to a patient's booking alone, so a staff
     * list bounded by the program would be narrower than what the create would accept, and an
     * emergency at eight in the evening would have nowhere to go. The server ignores it for a
     * patient caller, which is why passing it is safe rather than merely unhelpful there.
     *
     * `startStepMinutes` is the other half of making this a diary's list rather than a
     * patient's. The walk otherwise tiles windows back to back, which is right when somebody is
     * choosing *between* appointments — 09:00, 10:00, 11:00 — and wrong when somebody is
     * *placing* one, because an empty day would then offer only the o'clocks and half past nine
     * could not be said at all. The starts it produces are **aligned** to the hour rather than
     * merely spaced by the step, so a gap opening at 09:15 still offers 09:30 next.
     *
     * `excludeAppointmentId` reads past one appointment as though its hour were empty, for
     * moving that appointment — without it a booking occupies its own neighbourhood and cannot
     * be shifted by a quarter of an hour.
     */
    getEmptySlots(
        startDate: string,
        endDate: string,
        windowLength: number,
        therapistId: number,
        options: IEmptySlotOptions = {}
    ): UseQueryOptions<EmptySlotResponse[], Error> {
        return this.createPostQuery<EmptySlotResponse[]>('/appointments/empty-slots', {
            startDate,
            endDate,
            windowLength,
            therapistId,
            // Folded in only when asked for, so the body — and with it the query key — stays
            // byte-identical for every caller that wants neither.
            ...(options.includeOutsideProgram ? { includeOutsideProgram: true } : {}),
            ...(options.startStepMinutes
                ? { startStepMinutes: options.startStepMinutes }
                : {}),
            ...(options.excludeAppointmentId
                ? { excludeAppointmentId: options.excludeAppointmentId }
                : {}),
            ...(options.serviceId ? { serviceId: options.serviceId } : {}),
        });
    }

    /**
     * One appointment by id, for opening it from somewhere that holds only the id -- a line
     * in the bell. Staff only on the server, and scoped to the clinic.
     */
    getAppointment(appointmentId: number): UseQueryOptions<IAppointment, Error> {
        return this.createGetQuery<IAppointment>(`/appointments/${appointmentId}`);
    }

    /**
     * What may pay for this session, for the picker on its card.
     *
     * Its own read rather than a filter over `getPurchasesForPatient`, because the
     * question it answers cannot be answered here: which discipline a plan belongs to is
     * its catalogue option resolved up to a type, and `getServices()` returns singles
     * only — a package's own option is never in that list. The card used to offer
     * whichever plans had sessions left, which is how a package for another discipline
     * became the only thing a therapist could settle a session against.
     */
    getPaymentOptions(appointmentId: number): UseQueryOptions<IAppointmentPaymentOptions, Error> {
        return this.createGetQuery<IAppointmentPaymentOptions>(
            `/appointments/${appointmentId}/payment-options`
        );
    }

    /** Books one, records a walk-in (`status: 'HELD'`), or blocks out time (no `patientId`). */
    createAppointmentMutation(): UseMutationOptions<IAppointment, Error, CreateAppointmentRequest> {
        return this.createMutation<IAppointment, CreateAppointmentRequest>('/appointments');
    }

    /**
     * Marks one held, missed or off. SCHEDULED is how a mis-click is put back.
     *
     * Marking a patient's session held names what pays for it: the clinic takes payment as
     * the session ends, so settling and holding are one moment. `purchaseId` may be left
     * out only when the appointment already carries one -- a session booked against a
     * package bought earlier.
     *
     * The appointment is a mutation *variable* rather than baked into the URL at
     * construction, unlike the others here: a day view resolves whichever row the therapist
     * pressed, and a factory per row would mean a hook per row.
     */
    resolveAppointmentMutation(): UseMutationOptions<
        IAppointment,
        Error,
        { appointmentId: number; status: AppointmentStatus; purchaseId?: number }
    > {
        return {
            mutationFn: ({ appointmentId, status, purchaseId }): Promise<IAppointment> =>
                this.put<IAppointment>(`/appointments/${appointmentId}/status`, {
                    status,
                    ...(purchaseId ? { purchaseId } : {}),
                }),
        };
    }

    /**
     * The therapist agrees to a patient's request. Only the therapist it was made of, and
     * only before its deadline; the API refuses anybody else with a sentence worth showing.
     */
    confirmRequestMutation(): UseMutationOptions<IAppointment, Error, number> {
        return {
            mutationFn: (appointmentId: number): Promise<IAppointment> =>
                this.put<IAppointment>(`/appointments/${appointmentId}/confirm`),
        };
    }

    /** The therapist turns a request down, with an optional note for the patient's email. */
    declineRequestMutation(): UseMutationOptions<
        IAppointment,
        Error,
        { appointmentId: number } & DeclineAppointmentRequest
    > {
        return {
            mutationFn: ({ appointmentId, reason }): Promise<IAppointment> =>
                this.put<IAppointment>(`/appointments/${appointmentId}/decline`, {
                    ...(reason ? { reason } : {}),
                }),
        };
    }

    /** The patient takes back their own request while it is still waiting. */
    withdrawRequestMutation(): UseMutationOptions<IAppointment, Error, number> {
        return {
            mutationFn: (appointmentId: number): Promise<IAppointment> =>
                this.put<IAppointment>(`/appointments/${appointmentId}/withdraw`),
        };
    }

    /** Moves it, and optionally hands it to a colleague. */
    rescheduleMutation(
        appointmentId: number
    ): UseMutationOptions<IAppointment, Error, RescheduleAppointmentRequest> {
        return {
            mutationFn: (request: RescheduleAppointmentRequest): Promise<IAppointment> =>
                this.put<IAppointment>(`/appointments/${appointmentId}`, request),
        };
    }

    /**
     * Points a held appointment at the purchase that pays for it, or detaches it with a
     * null id. This is what settles a converted free evaluation or a session paid for
     * after it was given.
     */
    linkPurchaseMutation(
        appointmentId: number
    ): UseMutationOptions<IAppointment, Error, number | null> {
        return {
            mutationFn: (purchaseId: number | null): Promise<IAppointment> =>
                this.put<IAppointment>(`/appointments/${appointmentId}/purchase`, { purchaseId }),
        };
    }

    /**
     * Removes a mis-entered row outright. An appointment that will genuinely not happen is
     * cancelled instead, so that it stays visible.
     */
    deleteAppointmentMutation(): UseMutationOptions<void, Error, number> {
        return {
            mutationFn: (appointmentId: number): Promise<void> =>
                this.delete<void>(`/appointments/${appointmentId}`),
        };
    }
}
